import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Alert, AlertSeverity } from '../models';

import { AppService } from 'app/app.service';
import { MonitoringService } from 'app/monitor.service';
import { throwError } from 'rxjs';
import { NotificationService, NotificationSeverity } from '../services/notification.service';
import { Utils } from '@Utils';
import { AuthService } from '@auth0/auth0-angular';

@Injectable()
export class NotificationInterceptor implements HttpInterceptor {
    constructor(private notificationService: NotificationService,
        private monitoringService: MonitoringService,
        private appService: AppService,
        private authService: AuthService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            map((event: HttpEvent<any>) => {
                if (this.monitoringService.getRuleExecutionLogs().length > 0 && req.url.indexOf("executionlogs/rules") <= 0) {
                    const log = this.monitoringService.getRuleExecutionLogs()[0];
                    if (log.caseId) {
                        this.appService.flushRuleExecutionLogs(log.caseId, log.clientId).subscribe();
                    }
                }

                if (event instanceof HttpResponse && event.body && event.body.alerts && event.body.alerts.length > 0) {
                    const alerts = <Array<Alert>>event.body.alerts;

                    alerts.forEach(alert => {
                        let severity: NotificationSeverity = NotificationSeverity.Info;
                        let action = Utils.noop;

                        switch (alert.level) {
                            case AlertSeverity.Information:
                                severity = NotificationSeverity.Info;
                                action = undefined;
                                break;
                            case AlertSeverity.Warning:
                                severity = NotificationSeverity.Warning;
                                action = undefined;
                                break;
                            case AlertSeverity.Error:
                                severity = NotificationSeverity.Warning;
                                break;
                            case AlertSeverity.Critical:
                                severity = NotificationSeverity.Critical;
                                break;
                        }
                        this.notificationService.showNotification({
                            severity: severity,
                            message: alert.message,
                            log: alert.level === AlertSeverity.Error || alert.level === AlertSeverity.Critical,
                            action: action
                        });
                    });
                }
                return event;
            }),
            catchError((err: any, _caught) => {
                if (err instanceof HttpErrorResponse) {
                    if (err.status === 401) {
                        // Differentiate between authentication failures (should logout) and authorization failures (should not logout)
                        const shouldLogout = this.isAuthenticationFailure(err);

                        if (shouldLogout) {
                            // Handle authentication failure - user session is invalid or account is disabled
                            const errorMessage = this.getAuthenticationErrorMessage(err);

                            if (!errorMessage) {
                                // No explicit reason from the server (e.g. expired/missing token) - this is a
                                // normal session expiration, so just send the user back to the login page.
                                console.warn('401 Authentication failure - session expired, returning to login');
                                this.authService.logout({
                                    logoutParams: {
                                        returnTo: `${window.location.origin}/authorize?action=logout`
                                    }
                                });
                                return throwError(() => err);
                            }

                            console.error('401 Authentication failure - logging out with error:', errorMessage);

                            // Store the error in localStorage before logout. auth0-angular's logout
                            // only clears its own cache keys and does not call localStorage.clear(),
                            // so this value survives the cross-origin Auth0 redirect.
                            localStorage.setItem('uwpipeline.auth.error', errorMessage);

                            // Use the returnTo URL that matches Auth0's Allowed Logout URLs config.
                            this.authService.logout({
                                logoutParams: {
                                    returnTo: `${window.location.origin}/authorize?action=logout&error_description=${encodeURIComponent(errorMessage)}`
                                }
                            });
                        } else {
                            // Handle authorization failure - user doesn't have permission but is still authenticated
                            const errorMessage = err.error?.message || err.error || 'You do not have permission to perform this action.';

                            this.notificationService.showNotification({
                                severity: NotificationSeverity.Warning,
                                message: errorMessage
                            });

                            console.warn('401 Authorization failure:', errorMessage);
                        }
                    }
                    else if (err.status === 403) {
                        this.notificationService.showNotification({
                            severity: NotificationSeverity.Error,
                            message: err.error
                        });
                        console.error('err.error =', err.error, ';');
                    }
                    return throwError(() => err);
                }
            }));
    }

    /**
     * Returns the server-provided reason for an authentication failure (e.g. disabled/removed user),
     * or null when the server gave no explicit reason (e.g. expired or missing token).
     */
    private getAuthenticationErrorMessage(err: HttpErrorResponse): string | null {
        const error = err.error;

        if (!error) {
            return null;
        }

        if (typeof error === 'string') {
            return error.trim() || null;
        }

        return error.userErrors?.[0] || error.message || null;
    }

    /**
     * Determines if a 401 error is an authentication failure (should logout) or authorization failure (should not logout)
     * @param err The HTTP error response
     * @returns true if it's an authentication failure that requires logout
     */
    private isAuthenticationFailure(err: HttpErrorResponse): boolean {
        // Check for custom response property indicating authentication failure
        if (err.error?.isAuthenticationFailure === true) {
            return true;
        }

        // Check for custom header indicating authentication failure
        const authErrorType = err.headers.get('X-Auth-Error-Type');
        if (authErrorType === 'authentication-failure' || authErrorType === 'session-expired') {
            return true;
        }

        // Check error message for authentication-related keywords
        const errorMessage = (err.error?.message || err.error?.userErrors?.[0] || '').toLowerCase();
        const authenticationKeywords = [
            'disabled',
            'blocked',
            'suspended',
            'deactivated',
            'session expired',
            'session invalid',
            'token expired',
            'token invalid',
            'not authenticated',
            'authentication failed',
            'login required'
        ];

        if (authenticationKeywords.some(keyword => errorMessage.includes(keyword))) {
            return true;
        }

        // Check if it's a token refresh or auth-related endpoint (these always indicate auth failure)
        const authEndpoints = ['/token', '/refresh', '/auth', '/login', '/authorize'];
        if (authEndpoints.some(endpoint => err.url?.toLowerCase().includes(endpoint))) {
            return true;
        }

        // Check for specific error codes that indicate authentication failure
        const errorCode = err.error?.code || err.error?.errorCode;
        const authErrorCodes = ['AUTH_REQUIRED', 'SESSION_EXPIRED', 'TOKEN_EXPIRED', 'ACCOUNT_DISABLED', 'AUTHENTICATION_FAILED'];
        if (errorCode && authErrorCodes.includes(errorCode)) {
            return true;
        }

        // Default to NOT logging out for 401s (assume it's an authorization issue)
        // This is safer as it won't log users out unnecessarily
        return false;
    }
}
